/**
 * Metrics API — typed calls over the shared axios instance (contracts-plan.md
 * Appendix A "Metrics", §13.8 WP-72; §10.1, §10.2, §10.3).
 *
 * Per the ixverify precedent ("No new HTTP client, no ungated call") this file
 * never imports axios directly — only `services/api`, exactly the way
 * `frontend/src/contracts/api/rateCard.ts` does. It is a SEPARATE module (not
 * an addition to client.ts) because another WP appends to client.ts
 * concurrently (the intake.ts/rateCard.ts/notes.ts precedent) — a later mount
 * step may re-export these names from client.ts (metrics.mount.md).
 *
 * Types mirror `backend/src/services/contracts/metrics/README.md`'s
 * documented shapes.
 */
import api from '../../services/api';

export type MetricLabel = 'extracted' | 'derived' | 'inferred';

/** One `cta_metrics` row, re-hydrated with its label and formula text at read time (G8; the row itself never stores either). */
export interface MetricValue {
  offered_value: number | null;
  final_value: number | null;
  delta_abs: number | null;
  delta_pct: number | null;
  label: MetricLabel;
  formula: string | null;
  provenance: {
    version_ids: string[];
    line_ids: string[];
    // WP-87, ledger item 122: the cell a printed total (cpp_printed_*) was
    // read from — present only when the value came from a named cell.
    sheet?: string | null;
    cell?: string | null;
  } | null;
}

/** metric_key -> its value (§10.2's whole set, one negotiation). */
export type MetricsByKey = Record<string, MetricValue>;

/** One row of the Outcomes list — one negotiation. */
export interface MetricsListRow {
  negotiation_id: string;
  study_id: string;
  protocol_number: string | null;
  sponsor_id: string | null;
  cro_id: string | null;
  therapeutic_area_id: string | null;
  indication_id: string | null;
  recruitment_difficulty: number | null;
  /** The calendar year of fe_at, else agreed_at, else received_at; the string 'unknown' (never 0) when all three are null. */
  year: number | 'unknown';
  complete: boolean;
  incomplete_reason: string | null;
  metrics: MetricsByKey;
}

/** mean/median/n for one metric key inside one group (orchestrator decision: median of an even count is the mean of the two middle values; nulls excluded, never coerced to 0). */
export interface MetricsGroupStat {
  mean_offered: number | null;
  median_offered: number | null;
  mean_final: number | null;
  median_final: number | null;
  mean_delta_abs: number | null;
  median_delta_abs: number | null;
  /** The count that actually contributed to THIS metric's mean/median — may be smaller than the group's own n. */
  n: number;
}

export interface MetricsGroup {
  key: string;
  label: string;
  /** The group's own member count (negotiations), not a per-metric n — see MetricsGroupStat.n for that. */
  n: number;
  metrics: Record<string, MetricsGroupStat>;
}

export interface ListMetricsResult {
  count: number;
  rows: MetricsListRow[];
  groups: MetricsGroup[] | null;
}

export interface NegotiationMetricsResult {
  negotiation_id: string;
  metrics: MetricsByKey;
}

export interface RecomputeMetricsResult {
  negotiation_id: string;
  complete: boolean;
  incomplete_reason: string | null;
  stored: number;
  rows: unknown[];
}

export type MetricsSortKey = string;
export interface MetricsSort {
  key: MetricsSortKey;
  direction: 'asc' | 'desc';
}

export interface MetricsFilter {
  sponsorId?: string;
  croId?: string;
  indicationId?: string;
  therapeuticAreaId?: string;
  year?: number | 'unknown';
  recruitmentDifficulty?: number;
  complete?: boolean;
}

export type MetricsGroupBy = 'sponsor' | 'indication' | 'therapeutic_area' | 'cro' | 'year' | 'recruitment_difficulty';

export interface GetMetricsParams {
  sort?: MetricsSort;
  filter?: MetricsFilter;
  groupBy?: MetricsGroupBy;
}

/** GET /metrics?sort&filter (also accepts groupBy) — one row per negotiation of the caller's own site. */
export async function getMetrics(params: GetMetricsParams = {}): Promise<ListMetricsResult> {
  const query: Record<string, string> = {};
  if (params.sort) query.sort = JSON.stringify(params.sort);
  if (params.filter && Object.keys(params.filter).length > 0) query.filter = JSON.stringify(params.filter);
  if (params.groupBy) query.groupBy = params.groupBy;
  const response = await api.get<ListMetricsResult>('/api/contracts/metrics', { params: query });
  return response.data;
}

/** GET /negotiations/:id/metrics — the stored §10.2 set for one negotiation. */
export async function getNegotiationMetrics(negotiationId: string): Promise<NegotiationMetricsResult> {
  const response = await api.get<NegotiationMetricsResult>(`/api/contracts/negotiations/${negotiationId}/metrics`);
  return response.data;
}

/** POST /negotiations/:id/metrics/recompute — recomputes and replaces the whole stored set for one negotiation. */
export async function recomputeMetrics(negotiationId: string): Promise<RecomputeMetricsResult> {
  const response = await api.post<RecomputeMetricsResult>(`/api/contracts/negotiations/${negotiationId}/metrics/recompute`);
  return response.data;
}

export default { getMetrics, getNegotiationMetrics, recomputeMetrics };
